import React, { useState, useEffect } from 'react';
import api from '../config/api';

function BacklogList() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await api.get('/backlog-proposals');
      setProposals(response.data);
    } catch (error) {
      console.error('Error fetching backlog proposals:', error);
      setError('Failed to load backlog proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedProposals = () => {
    return [...proposals].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to string for comparison if needed
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      return sortDirection === 'desc' ? comparison * -1 : comparison;
    });
  };

  const handleCreateProposal = () => {
    setEditingProposal(null);
    setShowModal(true);
  };

  const handleEditProposal = (proposal) => {
    setEditingProposal(proposal);
    setShowModal(true);
  };

  const handleDeleteProposal = async (id) => {
    if (!window.confirm('Are you sure you want to delete this proposal?')) {
      return;
    }

    try {
      await api.delete(`/backlog-proposals/${id}`);
      await fetchProposals();
    } catch (error) {
      console.error('Error deleting proposal:', error);
      setError('Failed to delete proposal');
    }
  };

  const handleModalSave = async (proposalData) => {
    try {
      if (editingProposal) {
        await api.put(`/backlog-proposals/${editingProposal.id}`, proposalData);
      } else {
        await api.post('/backlog-proposals', proposalData);
      }
      await fetchProposals();
      setShowModal(false);
      setEditingProposal(null);
    } catch (error) {
      console.error('Error saving proposal:', error);
      setError('Failed to save proposal');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#e74c3c';
      case 'high': return '#e67e22';
      case 'medium': return '#f39c12';
      case 'low': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'proposed': return '#95a5a6';
      case 'accepted': return '#3498db';
      case 'in_progress': return '#f39c12';
      case 'testing': return '#e67e22';
      case 'completed': return '#27ae60';
      case 'rejected': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return <div>Loading backlog proposals...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Backlog Proposals</h2>
        <button className="btn" onClick={handleCreateProposal}>
          Create New Proposal
        </button>
      </div>

      {error && (
        <div style={{ 
          color: '#e74c3c', 
          backgroundColor: '#fdf2f2', 
          padding: '1rem', 
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {proposals.length === 0 ? (
        <div className="card">
          <p>No backlog proposals yet. Create your first proposal to track feature requests and improvements!</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderRadius: '4px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th 
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('title')}
                >
                  Title {getSortIcon('title')}
                </th>
                <th 
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('priority')}
                >
                  Priority {getSortIcon('priority')}
                </th>
                <th 
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('release_version')}
                >
                  Release {getSortIcon('release_version')}
                </th>
                <th 
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('status')}
                >
                  Status {getSortIcon('status')}
                </th>
                <th 
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    borderBottom: '2px solid #dee2e6',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={() => handleSort('created_at')}
                >
                  Created {getSortIcon('created_at')}
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '2px solid #dee2e6'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedProposals().map((proposal) => (
                <tr key={proposal.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                    <div>
                      <strong>{proposal.title}</strong>
                      {proposal.description && (
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: '#666', 
                          marginTop: '0.25rem',
                          maxWidth: '300px',
                          wordBreak: 'break-word'
                        }}>
                          {proposal.description.length > 100 
                            ? proposal.description.substring(0, 100) + '...'
                            : proposal.description
                          }
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: getPriorityColor(proposal.priority)
                    }}>
                      {proposal.priority?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {proposal.release_version || '-'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: getStatusColor(proposal.status)
                    }}>
                      {proposal.status?.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {formatDate(proposal.created_at)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => handleEditProposal(proposal)}
                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={() => handleDeleteProposal(proposal.id)}
                        style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <BacklogProposalModal
          proposal={editingProposal}
          onSave={handleModalSave}
          onCancel={() => {
            setShowModal(false);
            setEditingProposal(null);
          }}
        />
      )}
    </div>
  );
}

// Modal component for creating/editing proposals
function BacklogProposalModal({ proposal, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: proposal?.title || '',
    description: proposal?.description || '',
    priority: proposal?.priority || 'medium',
    release_version: proposal?.release_version || '',
    status: proposal?.status || 'proposed'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    onSave(formData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '600px' }}>
        <h3>{proposal ? 'Edit Proposal' : 'Create New Proposal'}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="Brief description of the proposal"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder="Detailed description of the proposal"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label>Release Version</label>
              <input
                type="text"
                name="release_version"
                value={formData.release_version}
                onChange={handleInputChange}
                placeholder="e.g., v1.2.0"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="proposed">Proposed</option>
                <option value="accepted">Accepted</option>
                <option value="in_progress">In Progress</option>
                <option value="testing">Testing</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '1rem', 
            marginTop: '2rem' 
          }}>
            <button type="button" className="btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn">
              {proposal ? 'Update' : 'Create'} Proposal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BacklogList;